import { LitElement, css, html, unsafeCSS } from 'lit';
import styles from './checkbox.scss?inline';
import { InputStyleController } from '@/controllers/InputStyleController';
import { createRef, ref } from 'lit/directives/ref.js';

export default class SotbitCheckboxStyle extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        title: { type: String },
        checked: { type: Boolean, state: true },
        disabled: { type: Boolean, state: true },
        readonly: { type: Boolean, state: true },
        indeterminate: { type: Boolean, state: true },
        hasIconLeftSlots: { type: Boolean, state: true },
        hasIconRightSlots: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.name = null;
        this.value = null;
        this.title = null;
        this.checked = false;
        this.disabled = false;
        this.readonly = false;
        this.input = null;
        this.hasIconLeftSlots = false;
        this.hasIconRightSlots = false;
        this.iconsLeft = [];
        this.iconsRight = [];
        this.imgSlotRef = createRef();

        this._controller = new InputStyleController(this, {
            getInput: () => {
                return this.input;
            },
        });
        this._controller.inputObserverConfig?.attributeFilter?.push('indeterminate');
        this._controller.inputObserverConfig?.attributeFilter?.push('checked');

        this._handleChange = this._handleChange.bind(this);
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);

        this.addEventListener('click', (e) => {
            this._handleLabelClick(e);
        });
    }

    connectedCallback() {
        super.connectedCallback();

        setTimeout(() => {
            if (this.input) {
                this._controller?.inputObserver?.observe(this.input, this._controller.inputObserverConfig);
                this._controller?.calcInputAttr();
            }
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._controller?.inputObserver?.disconnect();
    }

    willUpdate(changedProperties) {
        super.willUpdate(changedProperties);

        if (changedProperties.has('checked')) {
            this._setSelectedForIconsSlots([...this.iconsLeft, ...this.iconsRight]);
        }
    }

    _handleChange() {
        this.checked = this.input?.checked;
    }

    _handleLabelClick() {
        if (this.disabled || this.readonly || !this.input) {
            return;
        }

        this.input.checked = !this.input.checked;
        this.input.dispatchEvent(
            new CustomEvent('change', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleInputSlotChange(e) {
        const inputSlotElements = e.currentTarget.assignedElements();
        this.input = inputSlotElements[0];

        this._controller?.inputObserver?.disconnect();

        if (this.input) {
            this.input.removeEventListener('change', this._handleChange);
            this.input.addEventListener('change', this._handleChange);

            this._controller?.initHandlers();
            this._controller?.inputObserver.observe(this.input, this._controller?.inputObserverConfig);
            this._controller?.calcInputAttr();

            this.checked = this.input?.checked;

            if (!this.input.getAttribute('tabindex')) {
                this.input.setAttribute('tabindex', '0');
            }
        }
    }

    getIconName() {
        return this.indeterminate ? 'checkbox-indeterminate' : this.checked ? 'checkbox' : 'checkbox-outline';
    }

    getIconClassName() {
        return this.indeterminate ? 'icon-indeterminate' : this.checked ? 'icon-checked' : 'icon-unchecked';
    }

    _setSelectedForIconsSlots(icons) {
        for (const i in icons) {
            const icon = icons[i];

            icon.toggleAttribute('selected', this.checked);
        }
    }

    _handleIconLeftSlotChange({ currentTarget }) {
        this.iconsLeft = currentTarget.assignedElements({ flatten: true });
        this.hasIconLeftSlots = this.iconsLeft.length > 0;

        this._setSelectedForIconsSlots(this.iconsLeft);
    }

    _handleRightLeftSlotChange({ currentTarget }) {
        this.iconsRight = currentTarget.assignedElements({ flatten: true });
        this.hasIconRightSlots = this.iconsRight.length > 0;

        this._setSelectedForIconsSlots(this.iconsRight);
    }

    getWrapperClasses() {
        const classes = [];
        const states = ['disabled', 'readonly', 'checked'];

        for (const i in states) {
            const state = states[i];

            if (this[state]) {
                classes.push(state);
            }
        }

        if (this.hasIconLeftSlots || this.hasIconRightSlots) {
            classes.push('center');
        }

        return classes;
    }

    render() {
        const classes = this.getWrapperClasses();

        return html`
            <div class="checkbox-wrapper ${classes.join(' ')}">
                <span class="icon-box">
                    <slot name="checkbox" @slotchange="${this._handleInputSlotChange}"></slot>
                    <sotbit-icon
                        class="${this.getIconClassName()}"
                        icon="${this.getIconName()}"
                        variant="simple"
                    ></sotbit-icon>
                </span>
                <slot ${ref(this.imgSlotRef)} @slotchange="${this._handleIconLeftSlotChange}" name="icon-left"></slot>
                <div class="label-wrapper">
                    <slot name="label"></slot>
                </div>
                <slot ${ref(this.imgSlotRef)} @slotchange="${this._handleRightLeftSlotChange}" name="icon-right"></slot>
            </div>
        `;
    }
}
